<!--
  A related-records tab on the read page, backed by BreadDataRecordList.

  It was a BreadDataTable. A relation tab is a "which ones are attached?"
  question, and a table answered it by spending most of its width on repeating
  column headers above a handful of rows — so the list renders one row per
  related record with its name leading instead. The component name is unchanged
  because ~15 call sites and the model files reference it; only what it renders
  moved. The search bar and the request itself are untouched.

  Both ways of changing the relation now happen over the tab rather than on a
  page of their own — the relation is the context, and neither a two-field form
  nor a one-sentence confirmation justified losing it:

    · the add / attach button opens BreadModelChildAddDialog
    · a row's Detach action opens BreadModelChildDetachDialog, reaching it
      through the store because that button is mounted by a column formatter

  Both send what their pages sent. The /…/new and /…/detach routes still
  resolve, for links that point straight at them.

  Replaces BreadTabulatorTabChildV2 AND BreadTabulatorTabIntermediate, which
  were near-identical: both built the child model's browse columns, both hit the
  SAME browse URL (the "intermediate" one was a byte-for-byte copy of the child
  one), and differed only in the label on the add button and one disabled rule.
  Those two differences are the `relationship` prop and `isTeamAdmin` here.

  The model files are untouched. BreadDataRecordList runs their Tabulator column
  definitions through the same adapter the table uses and renders the Actions
  column through the same BreadDataTableCell, so the Detach formatter keeps
  working as written.
-->
<template>
  <div class="rtab">
    <div class="rtab__bar">
      <div class="rtab__search">
        <BreadTabulatorSearchable
          v-if="searchModel"
          ref="search"
          :model="searchModel"
          :setConditions="setConditions"
        ></BreadTabulatorSearchable>
      </div>

      <v-btn
        class="rtab__add"
        depressed
        :disabled="isAddDisabled"
        @click="addOpen = true"
      >
        <v-icon size="16" left>{{
          isIntermediate ? "mdi-link-variant-plus" : "mdi-plus"
        }}</v-icon>
        {{ addLabel }}
      </v-btn>
    </div>

    <!-- Adding happens over the tab rather than on a page of its own: the
         relation is the context, and routing away to the child-add page and
         back cost that context for a two-field form. Same BreadModelForm, same
         store URL, same payload — see BreadModelChildAddDialog. -->
    <BreadModelChildAddDialog
      v-model="addOpen"
      :parent="parent"
      :parentModel="parentModel"
      :childModelKey="childModelKey"
      :title="addLabel"
      :submitLabel="isIntermediate ? 'Attach' : 'Create'"
      :successMessage="isIntermediate ? 'Attached successfully.' : 'Created successfully.'"
      @created="onChanged"
    />

    <!-- Detaching confirms here for the same reason. The row's Detach button is
         mounted by a column formatter and reaches this through the store, so
         hosting the dialog is also what tells that button not to route. -->
    <BreadModelChildDetachDialog
      :contextModelKey="parentModel.key"
      @detached="onChanged"
    />

    <BreadDataRecordList
      ref="table"
      :apiUrl="apiGetBrowseUrl()"
      :columns="columns"
      :childPrefix="childPrefix"
      :parentPrefix="parentPrefix"
      :hiddenFields="hiddenFields"
      :emptyText="emptyText"
      :paginationSize="paginationSize"
      @total="$emit('total', $event)"
    ></BreadDataRecordList>
  </div>
</template>

<script>
import BreadModelChildAddDialog from "@/components/common/BreadModelChildAddDialog.vue";
import BreadModelChildDetachDialog from "@/components/common/BreadModelChildDetachDialog.vue";
import Model from "@/objects/model";
import Service from "@/objects/service";

export default {
  name: "BreadDataTableTabChild",
  components: {
    BreadModelChildAddDialog,
    BreadModelChildDetachDialog,
  },
  props: {
    childModelKey: {
      type: String,
      default: null,
    },
    tabProperties: {
      type: Object,
      default: () => ({}),
    },
    parent: {
      type: Object,
      default: () => ({}),
    },
    parentModel: {
      type: Object,
      default: () => ({}),
    },
    /** "child" or "intermediate" — only affects the add button's wording. */
    relationship: {
      type: String,
      default: "child",
    },
    isTeamAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    service: new Service(),
    childModel: new Model(),
    paginationSize: 10,
    columns: [],
    addOpen: false,
  }),
  computed: {
    isIntermediate() {
      return this.relationship === "intermediate";
    },
    /**
     * Intermediate models name themselves differently depending on which side
     * you are looking from — internalRoleToInternalUser is "Roles" under a user
     * and "Users" under a role — which is what `name[parentModel.key]` holds.
     */
    childLabel() {
      const names = this.childModel.name || {};
      const scoped = names[this.parentModel.key];
      return (scoped || names).singular;
    },
    addLabel() {
      return `${this.isIntermediate ? "Attach" : "New"} ${this.childLabel}`;
    },
    emptyText() {
      const names = this.childModel.name || {};
      const scoped = names[this.parentModel.key] || names;
      return `No ${(scoped.plural || "records").toLowerCase()} yet.`;
    },
    /**
     * The model the tab is really about. On an intermediate relation that is
     * the far side of the join table (a user's Roles tab lists internalRole),
     * which is what should title each row; on a direct child it is the child
     * itself.
     */
    childPrefix() {
      const key = this.tabProperties.intermediateChildModelKey || this.childModelKey;
      return key ? this.$_ucfirst(key) : "";
    },
    /** The record whose page we're on — repeating it on every row says nothing. */
    parentPrefix() {
      return this.parentModel.key ? this.$_ucfirst(this.parentModel.key) : "";
    },
    /**
     * The relation key the browse URL already filters by, so it holds the same
     * value on every row. Only `mapping.child` is a column on these rows;
     * `mapping.parent` names a field of the PARENT record and could collide
     * with a real child column of the same name.
     */
    hiddenFields() {
      const mapping = this.tabProperties.mapping || {};
      return mapping.child ? [mapping.child] : [];
    },
    /**
     * Team membership is administered by that team's own admins. Carried over
     * verbatim from BreadTabulatorTabIntermediate.
     */
    isAddDisabled() {
      return this.childModel.key === "ssoTeamToInternalUser" && !this.isTeamAdmin;
    },
    /**
     * BreadTabulatorSearchable offers every header whose `searchable` is not
     * false — which includes the Action column, since it declares no `searchable`
     * key and no `field` either. Handing it a model whose headers are already
     * narrowed to the real searchable fields keeps a dead "Action" entry out of
     * the column picker without touching the shared component, and returning
     * null when nothing qualifies hides the bar entirely.
     */
    searchModel() {
      const browse = this.childModel.browse || {};
      const table = browse.table || {};
      const headers = (table.headers || []).filter(
        (h) => h && h.field && h.searchable !== false
      );
      if (!headers.length) return null;
      return Object.assign({}, this.childModel, {
        browse: Object.assign({}, browse, {
          table: Object.assign({}, table, { headers }),
        }),
      });
    },
  },
  created() {
    this.childModel.getByKey(this.childModelKey);
    if (this.childModel.serviceKey)
      this.service.set("key", this.childModel.serviceKey);

    this.columns = this.$_.filter(
      this.childModel.browse.table.headers,
      (headers) => {
        return headers;
      }
    );

    // A `dataType: "modelParent"` formatter and its download accessor used to be
    // patched onto the shared column defs here. BreadDataRecordList renders
    // primary and meta values as text and offers no export, so neither was ever
    // called — and patching them mutated the module-singleton model, leaking
    // into every other page that reads it. Gone with the table.
  },
  methods: {
    apiGetBrowseUrl() {
      const parentRelationKey = this.tabProperties.mapping.parent;
      return `${this.$service[this.service.key]}/v1/en/console/tabulator/model/${
        this.parentModel.key
      }/${parentRelationKey}/${
        this.parent[parentRelationKey]
      }/child/${this.childModelKey}/browse/all`;
    },
    setConditions(conditions) {
      this.$refs.table.getFilterCondition(conditions);
    },
    /**
     * A row was added or removed on the server; reload the list in place. The
     * event goes up as well because the tab strip's count prefers the PARENT
     * record's `_count` over the total reported here, and only a re-read of
     * that record moves it.
     */
    onChanged() {
      if (this.$refs.table) this.$refs.table.refresh();
      this.$emit("changed");
    },
  },
};
</script>

<style scoped>
.rtab {
  padding: 16px;
}

.rtab__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.rtab__search {
  min-width: 0;
}

.rtab__add.v-btn {
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  box-shadow: none;
}

.rtab__add.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

.rtab__add.v-btn.v-btn--disabled {
  background: var(--app-elevated) !important;
  color: var(--app-text-faint) !important;
}

@media (max-width: 599px) {
  .rtab {
    padding: 12px 10px;
  }

  .rtab__bar {
    flex-direction: column;
    align-items: stretch;
  }

  .rtab__add.v-btn {
    width: 100%;
  }
}
</style>
